import React, {createContext, useContext, useEffect, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import _ from 'lodash'
import {useGet} from 'restful-react'
import {Menu, Icon} from 'semantic-ui-react'
import {ISVGPieProps, SVGPie} from '@/features/plots'
import {AuthContext} from '@/features/auth'
import {MessageContext} from '@/features/errors'
import {useInterval} from 'react-use'
import './style.css'

export interface IUploadContext {
  catalogName: string
  canUpload: boolean
  isBusy: boolean
  files: IFileMap
  quota?: IUploadQuota
  status?: IUploadStatusMap
  statusTotal?: IUploadStatus
  addFiles: (file: File[]) => void
  setUploadCatalogName: (name: string) => void
}

export interface IUploadsProps {
  children: React.ReactNode
}

export interface IUploadStatusPieProps extends Omit<ISVGPieProps, 'data'> {
  status: IUploadStatus
}

export interface IFileMap {
  [name: string]: {
    _id: string
    title: string
    file: File
    url: string
  }
}

export interface IUploadStatusMap {
  [fname: string]: IUploadStatus
}

export interface IUploadStatus {
  uploading: number
  processing: number
  progress: number
  progress_total: number
  numErrors: number
  hasError: boolean
  isComplete: boolean
  processingStatus?: {[product: string]: IApiProductProcessingStatus}
}

export interface IUploadQuota {
  upload_max_size: number
  uploads_cnt: number
  uploads_max_cnt: number
}

export interface IApiProductProcessingStatus {
  product: string
  status: 'failure' | 'in-progress' | 'success'
  progress: number
  done_cnt: number
  failed_cnt: number
  started_cnt: number
  queued_cnt: number
  failed_feature_codes: string[]

  progress_total: number
}

export interface IApiProcessingStatus {
  items: {
    vionlabs_id: string
    ident: string
    type: string
    title: string
    products: IApiProductProcessingStatus[]
  }[]
}

export const UploadContext = createContext<IUploadContext>({
  canUpload: false,
  isBusy: false,
  files: {},
  catalogName: '',
  addFiles: () => {},
  setUploadCatalogName: () => {},
})

export function UploadStatusMenuItem() {
  const {statusTotal} = useContext(UploadContext)
  const navigate = useNavigate()
  if (!statusTotal) return null
  return (
    <Menu.Item onClick={() => navigate('/upload')}>
      <div className='upload-status'>
        {statusTotal.hasError ?
          <>
            <Icon
              name='ban'
              color='red'
            />
            Upload failed
          </>
        : <>
            <UploadStatusPie status={statusTotal} />
            pending...
          </>
        }
      </div>
    </Menu.Item>
  )
}

export function UploadStatusPie(props: IUploadStatusPieProps) {
  const {status, ...rest} = props
  const {progress, progress_total} = status
  return (
    <SVGPie
      radius={10}
      total={progress_total}
      {...rest}
      data={_.compact([
        progress ? {value: progress} : undefined,
        // uploading ? {value: uploading - processing, color: 'rgba(255, 255, 255, 0.5)'} : undefined,
      ])}
    />
  )
}

// Context wrapper
export function UploadContextWrapper(props: IUploadsProps) {
  const {children} = props

  const {upload_catalogs} = useContext(AuthContext)

  const {logError} = useContext(MessageContext)

  const [catalogName, setCatalogName] = useState(upload_catalogs[0])

  const {loading: isQuotasLoading, refetch: refetchQuotas} = useGet({
    lazy: true,
    path: `/api/upload/catalogs/${catalogName}/upload_quotas`,
    resolve: (res) => {
      setQuota(res)
    },
  })

  const {refetch: refetchAssetProgress, data: processingStatusRaw} = useGet<IApiProcessingStatus>({
    lazy: true,
    path: `/api/report/catalogs/${catalogName}/per-product-progresses`,
  })

  // const storageRef = useRef(new Storage());
  const [isBusy, setIsBusy] = useState(false)
  const [files, setFiles] = useState<IFileMap>({})
  const [status, setStatus] = useState<IUploadStatusMap>({})
  const [statusTotal, setStatusTotal] = useState<IUploadStatus>({
    uploading: 0,
    processing: 0,
    progress: 0,
    progress_total: 100,
    numErrors: 0,
    hasError: false,
    isComplete: false,
  })
  const [quota, setQuota] = useState<IUploadQuota>()

  useInterval(() => {
    if (!catalogName) return
    refetchAssetProgress()
  }, 30000)

  useEffect(() => {
    return () => {
      _.chain(files)
        .values()
        .forEach((f) => {
          URL.revokeObjectURL(f.url)
        })
        .value()
    }
  }, [])

  useEffect(() => {
    if (!catalogName) return
    setStatus({})
    refetchQuotas()
    refetchAssetProgress()
  }, [catalogName])

  useEffect(() => {
    const res = _.chain(status)
      .values()
      .filter((v) => !(v.isComplete || v.hasError))
      // .filter(v => !v.hasError)
      .reduce(
        (a, b) =>
          _.chain(_.union(_.keys(a), _.keys(b)))
            .map((k) => {
              const aVal: any = _.get(a, k) || 0
              const bVal: any = _.get(b, k) || 0
              if (k === 'isDone') return [k, aVal && bVal]
              else if (k === 'hasError') return [k, aVal || bVal]
              else if (_.isBoolean(aVal) && _.isBoolean(bVal)) return [k, aVal || bVal]
              else if (_.isNumber(aVal) && _.isNumber(bVal)) return [k, _.add(aVal, bVal)]
            })
            .compact()
            .fromPairs()
            .value() as IUploadStatus
      )
      .value()
    // console.log('!!!', status, res);
    setStatusTotal(res)
  }, [status])

  useEffect(() => {
    if (!processingStatusRaw) return
    const res = _.chain(processingStatusRaw)
      .get('items')
      .map((p) => {
        const _procStat = _.chain(p.products || [])
          .groupBy((v) => v.product)
          .toPairs()
          .map(([k, v]) => [k, _.first(v)])
          .fromPairs()
          .value()
        const _defaultSums: IApiProductProcessingStatus = {
          product: '',
          status: 'in-progress',
          done_cnt: 0,
          failed_cnt: 0,
          progress: 0,
          progress_total: 100,
          queued_cnt: 0,
          started_cnt: 0,
          failed_feature_codes: [],
        }
        const sums = _.chain(p.products && p.products.length > 0 ? p.products : [_defaultSums])
          .reduce((a, b) => {
            const status =
              a.status === 'success' && b.status === 'success' ? 'success'
              : a.status === 'failure' || b.status === 'failure' ? 'failure'
              : 'in-progress'
            return {
              product: '',
              status,
              progress: (a.progress || 0) + (b.progress || 0),
              progress_total: (a.progress_total || 100) + (b.progress_total || 100),
              started_cnt: (a.started_cnt || 0) + (b.started_cnt || 0),
              failed_cnt: (a.failed_cnt || 0) + (b.failed_cnt || 0),
              queued_cnt: (a.queued_cnt || 0) + (b.queued_cnt || 0),
              done_cnt: (a.done_cnt || 0) + (b.done_cnt || 0),
              failed_feature_codes: _.union(a.failed_feature_codes, b.failed_feature_codes),
            }
          })
          .value()
        const uploadStatus: IUploadStatus = {
          uploading: 1,
          progress: sums.progress,
          processing: sums.progress / (sums.progress_total || 0),
          numErrors: sums.failed_cnt,
          hasError: sums.failed_cnt > 0,
          isComplete: !sums.started_cnt && !sums.queued_cnt,
          progress_total: sums.progress_total,
          // progress_total,
          processingStatus: _procStat,
        }
        return [p.ident, uploadStatus]
      })
      .fromPairs()
      .value()
    updateFileStatus(res)
  }, [processingStatusRaw])

  const canUpload =
    (catalogName && quota && quota.uploads_cnt < quota.uploads_max_cnt) ||
    // && false
    false

  function fileToName(file: File) {
    return `${file.name}_${file.size}`
  }

  function updateFileStatus(status: {[ident: string]: Partial<IUploadStatus>}) {
    setStatus((prev) => {
      if (_.isEqual(prev, status)) return prev
      const res = {...prev}
      _.chain(status)
        .keys()
        .forEach((k) => {
          if (_.isEqual(prev[k], status[k])) return
          res[k] = {
            ...prev[k],
            ...status[k],
            // progress_total: 100,
          }
        })
        .value()
      return res
    })
  }

  async function handleUploadFile(file: File) {
    const fname = fileToName(file)
    let error = false
    let signData = undefined
    await fetch(
      `/api/upload/catalogs/${encodeURIComponent(catalogName)}/idents/${encodeURIComponent(fname)}/upload_url?content_length=${file.size}`
    )
      .then(async (res) => {
        if (res.status !== 200) {
          const resData = await res.json()
          logError(`Sign error: ${_.get(resData, 'detail')}`)
          error = true
          return
        }
        signData = await res.json()
      })
      .catch((reason) => {
        logError('Cant sign request', reason)
        error = true
      })
    // const signData = {
    //     upload_signed_url_v4: '',
    // };
    const upload_url = _.get(signData, 'upload_signed_url_v4')
    if (!upload_url) {
      // logError('No upload url received', signData);
      error = true
    }

    if (error) {
      updateFileStatus({
        [fname]: {
          hasError: true,
        },
      })
      return
    }

    // Writing file data to bucket
    if (!upload_url) return
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', upload_url)
    xhr.setRequestHeader('Content-Type', 'application/octet-stream')
    // xhr.onreadystatechange = (e) => {
    //     if (xhr.readyState === xhr.DONE) {}
    //     console.log('state', e);
    // }
    xhr.onerror = (e) => {
      logError('Upload error', e)
      updateFileStatus({
        [fname]: {
          hasError: true,
        },
      })
    }
    xhr.upload.onprogress = (e) => {
      if (!e.lengthComputable) return
      updateFileStatus({
        [fname]: {
          uploading: e.loaded / e.total,
        },
      })
    }
    xhr.send(file)

    await refetchQuotas()
  }

  async function addFiles(files: File[]) {
    if (!canUpload) return
    setIsBusy(true)
    for (let i = 0; i < files.length; i++) {
      await _addFile(files[i])
    }
    setIsBusy(false)
  }

  async function _addFile(file: File) {
    const fname = fileToName(file)
    if (_.has(files, fname)) return
    setFiles((prev) => ({
      ...prev,
      [fname]: {
        _id: fname,
        title: file.name,
        file,
        url: URL.createObjectURL(file),
      },
    }))
    updateFileStatus({
      [fname]: {
        uploading: 0,
        processing: 0,
      },
    })
    handleUploadFile(file)
  }

  return (
    <UploadContext.Provider
      value={{
        catalogName,
        canUpload,
        isBusy: isBusy || isQuotasLoading || processingStatusRaw === null,
        quota,
        files,
        status,
        statusTotal,
        addFiles,
        setUploadCatalogName: setCatalogName,
      }}
    >
      {children}
    </UploadContext.Provider>
  )
}
